import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Typography, Button } from 'antd';
import Cookies from 'js-cookie';
import { USER_ROLES } from 'constants/users.js';
import { AMPLITUDE_EVENT_TYPES, AMPLITUDE_USER_ACTIONS } from 'constants/amplitude';
import useTranslation from 'utils/hooks/useTranslation';
import useAmplitude from 'utils/hooks/useAmplitude';
// import useGetFaqLink from 'utils/hooks/useGetFaqLink.js';
import { useFeatureFlag } from 'components/Providers/FeatureFlag';
import {
  optOutDatesByCountry,
  activationDateByCountry,
  feeRangesByCountry,
  feeGuideLinksByCountry,
  optOutForm,
} from './optOutDatesByCountry.js';
import ExtenededModal from 'components/Shared/Modal';

import styles from './style.module.scss';

const SmsReminderEnrollmentModal = () => {
  const { currentCorporation, corporationRole } = useSelector(state => state.user);
  const { locale } = useSelector(state => state.settings);
  const { showFeature } = useFeatureFlag();
  const { translateText } = useTranslation();
  const { sendAmplitudeEvent } = useAmplitude();
  // const faqLink = useGetFaqLink();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const excludedUserRoles = useMemo(() => {
    return [USER_ROLES.REPORTING.value, USER_ROLES.SERVICE_ADMIN.value];
  }, []);

  const COOKIE_NAME = 'smsReminderEnrollmentModal';

  const getLangCode = locale => {
    if (!locale) return 'en';
    return locale.slice(0, 2).toLowerCase();
  };

  useEffect(() => {
    if (
      showFeature('smsReminder') &&
      currentCorporation &&
      !excludedUserRoles.includes(corporationRole) &&
      !Cookies.get(COOKIE_NAME)
    ) {
      setIsModalVisible(true);
    }
  }, [corporationRole, currentCorporation, showFeature, excludedUserRoles]);

  const handleClose = () => {
    sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.smsReminderEnrollmentModal, {
      userAction: AMPLITUDE_USER_ACTIONS.closeClicked,
    });

    Cookies.set(COOKIE_NAME, 'true');
    setIsModalVisible(false);
  };

  return (
    <ExtenededModal
      title={
        <Typography component="h3" className={styles.modalTitle}>
          {translateText('corporateAccounts.modal.smsEnrollment.title')}
        </Typography>
      }
      width={560}
      visible={isModalVisible}
      className={styles.modalContainer}
      closable={false}
      footer={[
        <div key="footer" className={styles.footer}>
          <Button key="close" onClick={() => handleClose()} className={`${styles.closeButton}`}>
            {translateText('corporateAccounts.common.status.close')}
          </Button>
        </div>,
      ]}
    >
      <div className={styles.content}>
        <Typography>
          {translateText('corporateAccounts.modal.smsEnrollment.content', {
            activationDate:
              currentCorporation?.country && locale
                ? activationDateByCountry[currentCorporation?.country][getLangCode(locale)]
                : '',
          })}
        </Typography>
        <Typography>
          {translateText('corporateAccounts.modal.smsEnrollment.deadline', {
            usingThisForm: (
              <a
                href={optOutForm[currentCorporation?.country]}
                target="_blank"
                rel="noopener noreferrer"
                className={`${styles.buttonAsLink} ${styles.hyperlink}`}
              >
                {translateText('corporateAccounts.modal.smsEnrollment.usingThisForm')}
              </a>
            ),
            optOutDate:
              currentCorporation?.country && locale
                ? optOutDatesByCountry[currentCorporation?.country][getLangCode(locale)]
                : '',
          })}
        </Typography>
        {/* More info is eliminated, but for now keep it in the codebase in case we need to re-add it later. */}
        {/* <Typography>
          {translateText('corporateAccounts.modal.smsEnrollment.moreInfo', {
            faqPage: (
              <a
                href={faqLink}
                target="_blank"
                rel="noopener noreferrer"
                className={`${styles.buttonAsLink} ${styles.hyperlink}`}
              >
                {translateText('corporateAccounts.common.faqPage')}
              </a>
            ),
          })}
        </Typography> */}
        <Typography>
          {currentCorporation?.country === 'DE' || currentCorporation?.country === 'CH'
            ? translateText('corporateAccounts.modal.smsEnrollment.fees.de', {
                guide: (
                  <a
                    href={feeGuideLinksByCountry[currentCorporation?.country]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${styles.buttonAsLink} ${styles.hyperlink}`}
                  >
                    {translateText('corporateAccounts.common.guide')}
                  </a>
                ),
                lowestFee: feeRangesByCountry[currentCorporation?.country]?.lowestFee,
              })
            : translateText('corporateAccounts.modal.smsEnrollment.fees', {
                guide: (
                  <a
                    href={feeGuideLinksByCountry[currentCorporation?.country]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${styles.buttonAsLink} ${styles.hyperlink}`}
                  >
                    {translateText('corporateAccounts.common.guide')}
                  </a>
                ),
                lowestFee: feeRangesByCountry[currentCorporation?.country]?.lowestFee,
                highestFee: feeRangesByCountry[currentCorporation?.country]?.highestFee,
              })}
        </Typography>
      </div>
    </ExtenededModal>
  );
};

export default SmsReminderEnrollmentModal;
